import { faClock, faMapMarkerAlt, faUsers, faSearch, faUserGroup, faComment } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import cn from 'classnames';
import { useSearchParamsUpdater } from 'hooks/useSearchParamsUpdater';
import { addNotification } from '@redux/features/notificationsSlice';
import { getEntries } from '@redux/features/entriesSlice';
import { getCoaches } from '@redux/features/coachesSlice';
import { getClients } from '@redux/features/clientsSlice';
import { getGroups } from '@redux/features/groupsSlice';
import { SERVICE_TYPES_UA } from 'constants/serviceTypes';
import { entryStatuses } from 'constants/entryStatuses';
import { CreateEntry } from 'pages/Schedule/CreateEntry';
import { Actions } from 'pages/Schedule/Actions';
import { Header } from 'pages/Schedule/Header';
import { PaymentsCell } from './PaymentsCell';
import { PersonCell } from './PersonCell';
import styles from 'pages/Schedule/Schedule.scss';

export const Schedule = () => {
  const { error, entries } = useSelector((state) => state.entries);
  const { locations } = useSelector((state) => state.locations);
  const { coaches } = useSelector((state) => state.coaches);
  const { clients } = useSelector((state) => state.clients);
  const { groups } = useSelector((state) => state.groups);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const query = useSearchParamsUpdater();
  const { locationName } = useParams();
  const dispatch = useDispatch();

  const locationTransliteration = query.getParam('location') || locationName;
  const searchDate = query.getParam('date');
  const hall = query.getParam('hall');

  useEffect(() => {
    if (clients.length <= 1) {
      dispatch(getClients());
    }
    if (coaches.length <= 1) {
      dispatch(getCoaches());
    }
    if (groups.length <= 1) {
      dispatch(getGroups({ expand: 'true' }));
    }
  }, []);

  useEffect(() => {
    if (error?.message) {
      dispatch(
        addNotification({
          id: Date.now(),
          title: error.message,
          type: 'error',
        })
      );
    }
  }, [error, dispatch]);

  const location = useMemo(
    () => locations.find(({ transliteration }) => transliteration === locationTransliteration),
    [locations, locationTransliteration]
  );

  useEffect(() => {
    if (location?.id && searchDate) {
      dispatch(getEntries({ locationID: location.id, date: searchDate, hallID: hall, sortOrder: 'asc' }));
    }
  }, [location, searchDate, hall]);

  const todayEntries = useMemo(() => entries.filter((entry) => entry.date === searchDate), [entries, searchDate]);

  const formatTime = (time) => time.slice(0, 5);

  const getStatus = (status) => entryStatuses.find(({ key }) => key === status);

  const isGroupBooking = (booking) => booking.group !== null;

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingEntry(null);
  };

  const isModalOpen = isCreateModalOpen;

  return (
    <>
      <Header isCreateModalOpen={isCreateModalOpen} setIsCreateModalOpen={setIsCreateModalOpen} />
      {isModalOpen && location && (
        <CreateEntry
          allCoaches={coaches}
          allClients={clients}
          groups={groups}
          currentDate={searchDate}
          locationID={location.id}
          halls={location?.halls}
          setIsOpen={handleCloseModal}
          editingEntry={editingEntry}
          tabIndex={isModalOpen ? 0 : -1}
        />
      )}
      <div className={styles.bookingsList}>
        {todayEntries.length === 0 ? (
          <div className={styles.empty}>
            <FontAwesomeIcon icon={faSearch} className={styles.icon} />
            <h3>Записи не знайдено</h3>
            <p>Спробуйте змінити параметри пошуку</p>
          </div>
        ) : (
          <div className={styles.table}>
            <div className={cn(styles.tableRow, styles.tableRowHeader)}>
              <div className={styles.tableCell}>Послуга</div>
              <div className={styles.tableCell}>Час</div>
              <div className={styles.tableCell}>Зала</div>
              <div className={styles.tableCell}>Учасники</div>
              <div className={styles.tableCell}>Тренери</div>
              <div className={styles.tableCell}>Оплата</div>
              <div className={cn(styles.tableCell, styles.tableCellStatus)}>Статус</div>
            </div>

            {todayEntries.map((booking) => (
              <div className={styles.tableRow} key={booking.id}>
                <div className={cn(styles.tableCell, styles.service)}>
                  <FontAwesomeIcon icon={isGroupBooking(booking) ? faUsers : faUserGroup} className={styles.icon} />
                  <div className={styles.info}>
                    <span className={styles.name}>{SERVICE_TYPES_UA[booking.service]}</span>
                    {booking.comment && (
                      <div className={styles.comment}>
                        <FontAwesomeIcon icon={faComment} />"{booking.comment}"
                      </div>
                    )}
                  </div>
                </div>

                <div className={cn(styles.tableCell, styles.time)}>
                  <FontAwesomeIcon icon={faClock} />
                  {formatTime(booking.startTime)} {booking.endTime && `- ${formatTime(booking.endTime)}`}
                </div>

                <div className={cn(styles.tableCell, styles.location)}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  <span className={styles.hallName}>{booking.hall.name}</span>
                </div>

                <PersonCell
                  booking={booking}
                  people={booking.participants}
                  isGroup={isGroupBooking(booking)}
                  role='client'
                />

                <PersonCell booking={booking} people={booking.coaches} isGroup={isGroupBooking(booking)} role='coach' />

                <PaymentsCell booking={booking} />

                <div className={cn(styles.tableCell, styles.tableCellStatus)}>
                  <div className={cn(styles.status, styles[getStatus(booking.status).key])}>
                    <FontAwesomeIcon icon={getStatus(booking.status).icon} />
                    {getStatus(booking.status).name}
                  </div>
                </div>

                <Actions className={styles.actions} entry={booking} onEdit={handleEditEntry} />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
